import React, { useState } from 'react';
import { Box, Button, Card, CardContent, CardMedia, Typography, Grid, IconButton, Select, MenuItem, FormControl, InputLabel, Chip, Tabs, Tab, Avatar, Rating, Divider } from '@mui/material';
import { Heart, ShoppingCart, Truck, Eye, Star, ChevronLeft, ChevronRight, Home, User } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

function ProductPage() {
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [zoom, setZoom] = useState(false);

  // Dummy product data
  const product = {
    id: 1,
    title: 'Premium Wireless Headphones',
    price: 299,
    originalPrice: 399,
    discount: 25,
    rating: 4.5,
    reviewCount: 128,
    description: 'Experience superior sound quality with our premium wireless headphones. Featuring active noise cancellation, 30-hour battery life, and comfortable over-ear design.',
    longDescription: 'These premium wireless headphones deliver studio-quality sound with deep bass and crystal-clear highs. The active noise cancellation technology blocks out unwanted ambient noise, allowing you to focus on your music. With a comfortable over-ear design and adjustable headband, you can wear them for hours without discomfort. The 30-hour battery life ensures you never run out of power, and the quick-charge feature gives you 3 hours of playback from just 10 minutes of charging.',
    images: [
      'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=600&h=600&fit=crop',
      'https://images.unsplash.com/photo-1484704849700-f032a568e944?w=600&h=600&fit=crop',
      'https://images.unsplash.com/photo-1583394838336-acd977736f90?w=600&h=600&fit=crop',
      'https://images.unsplash.com/photo-1498049794561-7780e7231661?w=600&h=600&fit=crop',
    ],
    sizes: ['S', 'M', 'L', 'XL'],
    colors: ['Black', 'White', 'Blue', 'Red'],
    stock: 3,
    viewers: 20,
    deliveryDate: 'March 15, 2026',
  };

  const reviews = [
    { id: 1, name: 'John D.', rating: 5, date: '2026-03-10', comment: 'Amazing sound quality! Worth every penny.', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=40&h=40&fit=crop&crop=face' },
    { id: 2, name: 'Sarah M.', rating: 4, date: '2026-03-08', comment: 'Great noise cancellation, very comfortable for long sessions.', avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=40&h=40&fit=crop&crop=face' },
    { id: 3, name: 'Mike R.', rating: 5, date: '2026-03-05', comment: 'Battery life is incredible. Highly recommend!', avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face' },
  ];

  const relatedProducts = [
    { id: 2, title: 'Wireless Earbuds', price: 149, rating: 4.3, image: 'https://images.unsplash.com/photo-1572569511254-d8f925fe2cbb?w=300&h=300&fit=crop' },
    { id: 3, title: 'Bluetooth Speaker', price: 89, rating: 4.7, image: 'https://images.unsplash.com/photo-1608043152269-423dbba4e7e1?w=300&h=300&fit=crop' },
    { id: 4, title: 'Phone Case', price: 29, rating: 4.1, image: 'https://images.unsplash.com/photo-1565849904461-04a58ad377e0?w=300&h=300&fit=crop' },
  ];

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const nextImage = () => {
    setSelectedImage((prev) => (prev + 1) % product.images.length);
  };

  const prevImage = () => {
    setSelectedImage((prev) => (prev - 1 + product.images.length) % product.images.length);
  };

  return (
    <Box sx={{ mt: 7, mx: 2, maxWidth: '1200px', mx: 'auto' }}>
      {/* Simple Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, p: 2, bgcolor: 'white', borderRadius: 1, boxShadow: 1 }}>
        <Button startIcon={<Home />} onClick={() => navigate('/Ecommerce')}>Home</Button>
        <Typography variant="h5">Product Details</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={() => navigate('/CartPage')}><ShoppingCart /></IconButton>
          <IconButton onClick={() => navigate('/UserDashboard')}><User /></IconButton>
        </Box>
      </Box>
      <Grid container spacing={4}>
        {/* Left: Product Images */}
        <Grid item xs={12} md={6}>
          <Box sx={{ position: 'relative' }}>
            <Box
              component={motion.div}
              whileHover={{ scale: zoom ? 1.2 : 1 }}
              sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 2,
                cursor: zoom ? 'zoom-out' : 'zoom-in',
                height: '500px',
              }}
              onClick={() => setZoom(!zoom)}
            >
              <Box
                component="img"
                src={product.images[selectedImage]}
                alt={product.title}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  transition: 'transform 0.3s',
                  transform: zoom ? 'scale(1.2)' : 'scale(1)',
                }}
              />
            </Box>
            <IconButton
              onClick={prevImage}
              sx={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(255,255,255,0.8)' }}
            >
              <ChevronLeft />
            </IconButton>
            <IconButton
              onClick={nextImage}
              sx={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(255,255,255,0.8)' }}
            >
              <ChevronRight />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mt: 2, overflowX: 'auto' }}>
            {product.images.map((img, index) => (
              <Box
                key={index}
                component="img"
                src={img}
                alt={`Thumbnail ${index + 1}`}
                sx={{
                  width: 80,
                  height: 80,
                  objectFit: 'cover',
                  borderRadius: 1,
                  cursor: 'pointer',
                  border: selectedImage === index ? '2px solid #1976d2' : '2px solid transparent',
                }}
                onClick={() => setSelectedImage(index)}
              />
            ))}
          </Box>
        </Grid>

        {/* Right: Product Details */}
        <Grid item xs={12} md={6}>
          <Typography variant="h4" sx={{ mb: 1 }}>{product.title}</Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Rating value={product.rating} readOnly precision={0.5} />
            <Typography variant="body2">({product.reviewCount} reviews)</Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="h5" color="primary">${product.price}</Typography>
            <Typography variant="body1" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>${product.originalPrice}</Typography>
            <Chip label={`${product.discount}% off`} color="secondary" size="small" />
          </Box>

          <Typography variant="body1" sx={{ mb: 3 }}>{product.description}</Typography>

          {/* Psychological triggers */}
          <Box sx={{ mb: 3, p: 2, bgcolor: '#fff3cd', borderRadius: 1 }}>
            <Typography variant="body2" color="warning.main" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Eye size={16} /> 🔥 {product.viewers} people are viewing this product
            </Typography>
            <Typography variant="body2" color="error.main" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              ⚠️ Only {product.stock} left in stock
            </Typography>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Truck size={16} /> Get it by {product.deliveryDate}
            </Typography>
          </Box>

          {/* Size and Color Selection */}
          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Size</InputLabel>
              <Select value={selectedSize} onChange={(e) => setSelectedSize(e.target.value)}>
                {product.sizes.map(size => (
                  <MenuItem key={size} value={size}>{size}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Color</InputLabel>
              <Select value={selectedColor} onChange={(e) => setSelectedColor(e.target.value)}>
                {product.colors.map(color => (
                  <MenuItem key={color} value={color}>{color}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Button variant="outlined" startIcon={<Heart />} fullWidth>
              Add to Wishlist
            </Button>
            <Button variant="contained" startIcon={<ShoppingCart />} fullWidth>
              Add to Cart
            </Button>
          </Box>
          <Button variant="contained" color="secondary" size="large" fullWidth sx={{ mb: 3 }}>
            Buy Now
          </Button>
        </Grid>
      </Grid>

      {/* Tabs: Description and Reviews */}
      <Box sx={{ mt: 4 }}>
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Description" />
          <Tab label={`Reviews (${product.reviewCount})`} />
        </Tabs>

        <Box sx={{ mt: 3 }}>
          {tabValue === 0 && (
            <Typography variant="body1">{product.longDescription}</Typography>
          )}
          {tabValue === 1 && (
            <Box>
              {reviews.map(review => (
                <Box key={review.id} sx={{ mb: 3, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <Avatar src={review.avatar} sx={{ width: 40, height: 40 }} />
                    <Box>
                      <Typography variant="subtitle1">{review.name}</Typography>
                      <Rating value={review.rating} readOnly size="small" />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>{review.date}</Typography>
                  </Box>
                  <Typography variant="body1">{review.comment}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      {/* Related Products */}
      <Box sx={{ mt: 6 }}>
        <Typography variant="h5" sx={{ mb: 3 }}>Related Products</Typography>
        <Grid container spacing={2}>
          {relatedProducts.map((relatedProduct) => (
            <Grid item xs={12} sm={6} md={4} key={relatedProduct.id}>
              <Card
                component={motion.div}
                whileHover={{ y: -5 }}
                sx={{ cursor: 'pointer' }}
              >
                <CardMedia component="img" height="200" image={relatedProduct.image} alt={relatedProduct.title} />
                <CardContent>
                  <Typography variant="h6">{relatedProduct.title}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="h6" color="primary">${relatedProduct.price}</Typography>
                    <Rating value={relatedProduct.rating} readOnly size="small" />
                  </Box>
                  <Button variant="outlined" fullWidth>Add to Cart</Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
}

export default ProductPage;